/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ID } from '@gh/common-shared-data';
import { DataMapper } from '@gh/core-data';

import { isFunction } from 'lodash';

import { EntityMerge, EntityReplace, EntityTransform } from '../entity/entity.utils';

export type TableGroupIndex<T> = Hash<T[]>;
export type TableOrderIndex<T> = T[];
export type TableIndex<T> = TableGroupIndex<T> | TableOrderIndex<T>;

export type TableEntityMerge<T> = EntityMerge<T>;
export type TableEntityReplace<T> = EntityReplace<T>;
export type TableEntityTransform<T> = EntityTransform<T>;

export type Table<T> = {
  isEmpty: boolean;
  rows: Hash<T>;
  indexes: Hash<TableIndex<T>>;
};

export type TableGetIdOp<T> = TransformFn<T, ID>;

export type TableCreatorOp<T> = (iterator?: Iterator<T>) => Table<T>;
export type TableInsertOp<T> = (table: Table<T>, data: T) => Table<T>;
export type TableUpdateOp<T> = (table: Table<T>, data: T) => Table<T>;
export type TableRemoveOp<T> = (table: Table<T>, id: ID) => Table<T>;

export type TableInsertAllOp<T> = (table: Table<T>, data: T[]) => Table<T>;
export type TableUpdateAllOp<T> = (table: Table<T>, data: T[]) => Table<T>;
export type TableRemoveAllOp<T> = (table: Table<T>, id: ID[]) => Table<T>;

export type TableMergeByIdOp<T> = (table: Table<T>,
                                   id: ID,
                                   transform: TableEntityTransform<T>) => Table<T>;
export type TableMergeAllByIdOp<T> = (table: Table<T>,
                                      id: ID[],
                                      transform: TableEntityTransform<T>) => Table<T>;

export type TableMergeOp<T> = (table: Table<T>,
                               data: T,
                               merge: TableEntityMerge<T>) => Table<T>;
export type TableMergeAllOp<T> = (table: Table<T>,
                                  data: T[],
                                  merge: TableEntityMerge<T>) => Table<T>;

export type TableReplaceAllOp<T> = (table: Table<T>,
                                    oldIds: ID[],
                                    newData: T[],
                                    merge: TableEntityReplace<T>) => Table<T>;

export type TableFindByIdOp<T> = (table: Table<T>, id: ID) => Maybe<T>;
export type TableFindByIdSetOp<T> = (table: Table<T>, ids: ID[]) => T[];
export type TableFindAllOp<T> = (table: Table<T>) => T[];
export type TableFindByGroupOp<T> = (table: Table<T>, group: string, value: any) => T[];
export type TableFindByOrderOp<T> = (table: Table<T>, order: string) => T[];

export type TableIsEmptyOp<T> = (table: Table<T>) => boolean;

export type TableMapperOfOp<T> = (entityMapper: DataMapper<T, any>) => DataMapper<Table<T>, any>;

export interface TableIndexBuilder<T> {
  insert(row: T): void;

  update(oldRow: T, newRow: T): void;

  remove(row: T): void;

  build(): TableIndex<T>;
}

export interface TableIndexDriver<T> {
  name: string;

  create(): TableIndex<T>;

  modify(index: TableIndex<T>): TableIndexBuilder<T>;
}

export interface TableGroupIndexDriver<T> extends TableIndexDriver<T> {
  getGroupedData(index: TableIndex<T>, value: any): T[];
}

export interface TableOrderIndexDriver<T> extends TableIndexDriver<T> {
  getOrderedData(index: TableIndex<T>): T[];
}

export type TableDriver<T> = {
  getId: TableGetIdOp<T>;
  create: TableCreatorOp<T>;

  insert: TableInsertOp<T>;
  update: TableUpdateOp<T>;
  remove: TableRemoveOp<T>;

  insertAll: TableInsertAllOp<T>;
  updateAll: TableUpdateAllOp<T>;
  removeAll: TableRemoveAllOp<T>;

  mergeById: TableMergeByIdOp<T>;
  mergeAllById: TableMergeAllByIdOp<T>;

  merge: TableMergeOp<T>;
  mergeAll: TableMergeAllOp<T>;

  replaceAll: TableReplaceAllOp<T>;

  all: TableFindAllOp<T>;
  byId: TableFindByIdOp<T>;
  byIdSet: TableFindByIdSetOp<T>;
  byGroup: TableFindByGroupOp<T>;
  byOrder: TableFindByOrderOp<T>;

  mapperOf: TableMapperOfOp<T>;

  isEmpty: TableIsEmptyOp<T>;
};

export type TableDriverFactory<T> = (getId: TransformFn<T, ID>,
                                     ...indexDrivers: TableIndexDriver<T>[]) => TableDriver<T>;

export function isTableGroupIndex<T>(indexDriver: TableIndexDriver<T>): indexDriver is TableGroupIndexDriver<T> {
  return isFunction(indexDriver['getGroupedData']);
}

export function isTableOrderIndex<T>(indexDriver: TableIndexDriver<T>): indexDriver is TableOrderIndexDriver<T> {
  return isFunction(indexDriver['getOrderedData']);
}
